import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { apiFetch } from '../utils/api';
import { teamAcronym } from '../utils/teamAcronyms';

const BAR_MAX = 0.15;

function DiffBar({ value }) {
  if (value == null) return <span className="text-muted">—</span>;

  const clamped = Math.max(-BAR_MAX, Math.min(BAR_MAX, value));
  const pct = Math.abs(clamped) / BAR_MAX * 50;
  const isPositive = value >= 0;

  return (
    <div style={{ minWidth: '120px' }}>
      <div className="position-relative w-100" style={{ height: '14px' }}>
        <div
          className="position-absolute top-0 bottom-0"
          style={{ left: '50%', width: '1px', backgroundColor: '#dee2e6' }}
        />
        {isPositive ? (
          <div
            className="position-absolute top-0 bottom-0 rounded-end"
            style={{
              left: '50%',
              width: `${pct}%`,
              backgroundColor: 'rgba(25, 135, 84, 0.7)',
            }}
          />
        ) : (
          <div
            className="position-absolute top-0 bottom-0 rounded-start"
            style={{
              right: '50%',
              width: `${pct}%`,
              backgroundColor: 'rgba(220, 53, 69, 0.7)',
            }}
          />
        )}
      </div>
    </div>
  );
}

function ResultBadge({ result, upcoming }) {
  if (upcoming) {
    return <span className="badge bg-info bg-opacity-50" style={{ width: '20px' }}>—</span>;
  }
  const colors = { W: 'success', D: 'secondary', L: 'danger' };
  return (
    <span className={`badge bg-${colors[result]} bg-opacity-75`} style={{ width: '20px' }}>
      {result}
    </span>
  );
}

export default function ExpectedPointsCard() {
  const { token } = useAuth();
  const { selectedTeam, selectedDate, modelDatesParam } = useDashboard();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !selectedTeam || !selectedDate) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ team: selectedTeam, date: selectedDate });
    if (modelDatesParam) params.set('modelDates', modelDatesParam);
    apiFetch(`/expected-points?${params}`, token)
      .then((data) => {
        if (cancelled) return;
        setRows(data.rows);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, selectedTeam, selectedDate, modelDatesParam]);

  const marketSource = rows.some((r) => r.pinnacleXPts != null) ? 'pinnacle' : 'bet365';

  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center">
        <strong>Expected Points</strong>
        <span className="ms-auto text-muted small">Last 6 matches</span>
      </div>
      <div className="card-body p-0">
        {loading && (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
        )}
        {error && (
          <div className="text-center py-4 text-danger small">{error}</div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="text-center py-4 text-muted small">No data available</div>
        )}
        {!loading && !error && rows.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm mb-0 small">
              <thead>
                <tr>
                  <th className="ps-3">Date</th>
                  <th>Opp</th>
                  <th className="text-center"></th>
                  <th className="text-center">Model</th>
                  <th className="text-center">Market</th>
                  <th className="ps-2">Diff (Market − Model)</th>
                </tr>
              </thead>
              <tbody>
                {[...rows].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6).map((row) => {
                  const marketXPts = marketSource === 'pinnacle' ? row.pinnacleXPts : row.bet365XPts;
                  const diff = (row.modelXPts != null && marketXPts != null)
                    ? Math.round((marketXPts - row.modelXPts) * 100) / 100
                    : null;

                  return (
                    <tr key={row.date + row.opponent} style={row.upcoming ? { opacity: 0.7, fontStyle: 'italic' } : undefined}>
                      <td className="ps-3 text-nowrap">{formatDate(row.date)}</td>
                      <td className="text-nowrap">
                        <span className="text-muted me-1" style={{ fontSize: '0.7em' }}>{row.venue}</span>
                        {teamAcronym(row.opponent)}
                      </td>
                      <td className="text-center">
                        <ResultBadge result={row.result} upcoming={row.upcoming} />
                      </td>
                      <td className="text-center">
                        {row.modelXPts != null ? row.modelXPts.toFixed(2) : '—'}
                      </td>
                      <td className="text-center">
                        {marketXPts != null ? marketXPts.toFixed(2) : '—'}
                      </td>
                      <td className="ps-2">
                        <DiffBar value={diff} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()}/${d.getMonth() + 1}`;
}
